import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import get from 'lodash/get';
import { DeleteOutline, DescriptionOutlined, Edit, VisibilityOffOutlined, FileDownload } from '@mui/icons-material';

import { useDeleteCostInvoicing, useGetCostsInvoicing } from 'api';
import navigationPaths from 'engine/navigationPaths';
import urls from 'engine/urls';

import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

import * as Styled from './CostPreview.styles';
import { DeleteConfirmationModal } from './components';
import { downloadFile } from 'utility/downloadFile';

export const CostPreview = () => {
  const { costId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(document.location.search);

  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [isHovered, setIsHovered] = useState({});

  const { data: costData, isLoading: isCostDataLoading } = useGetCostsInvoicing(costId, {
    onSuccess: (data) => {
      const attachments = get(data, 'invoice.attachments', []);
      const attachment = attachments.find((item) => item.id === searchParams.get('attachmentId'));
      setCurrentAttachment(attachment || attachments[0] || null);
    },
  });

  const { mutate: deleteCost, isLoading: isCostDeleting } = useDeleteCostInvoicing(costId, {
    onSuccess: () => {
      navigate(navigationPaths().CostInvoices);
    },
  });

  const downloadSelectedFile = () => {
    downloadFile(`${urls().root}${currentAttachment.uri}`, currentAttachment.contentType, currentAttachment.fileName);
  };

  const handleMouseEnter = (index) => {
    setIsHovered((prevState) => {
      return { ...prevState, [index]: true };
    });
  };

  const handleMouseLeave = (index) => {
    setIsHovered((prevState) => {
      return { ...prevState, [index]: false };
    });
  };

  return (
    <DashboardLayout isLoading={isCostDataLoading}>
      <DashboardNavbar />

      <Styled.Root preloading>
        <Styled.Card>
          {get(currentAttachment, 'fileName') && (
            <>
              <Styled.Title className="small">{get(currentAttachment, 'fileName')}</Styled.Title>
              <Styled.Divider />
            </>
          )}
          {get(currentAttachment, 'contentType') === 'application/pdf' ? (
            <div>
              <Styled.Pdf data={`${urls().root}${currentAttachment.uri}`} type="application/pdf" />
            </div>
          ) : get(currentAttachment, 'contentType', '').startsWith('image') ? (
            <img src={`${urls().root}${currentAttachment.uri}`} alt={currentAttachment.fileName} />
          ) : (
            <Styled.PreviewPlaceholder>
              <VisibilityOffOutlined />
              Brak podglądu
            </Styled.PreviewPlaceholder>
          )}
        </Styled.Card>

        <Styled.SideCard>
          <Styled.VerticalLeftAligned>
            <Styled.Title>Koszt {get(costData, 'invoice.fullNumber')}</Styled.Title>
          </Styled.VerticalLeftAligned>

          <Styled.Divider />

          {get(costData, 'invoice.attachments', []).length > 0 && (
            <>
              <Styled.VerticalLeftAligned>
                <Styled.Title className="small">Pliki</Styled.Title>

                <Styled.Files>
                  {get(costData, 'invoice.attachments', []).map((attachment, index) => (
                    <Styled.File
                      key={`${index}-${attachment.fileName}`}
                      onClick={() => setCurrentAttachment(attachment)}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={() => handleMouseLeave(index)}
                    >
                      {!isHovered[index] && <DescriptionOutlined />}
                      {isHovered[index] && <Styled.PreviewIcon />}
                      <span>{attachment.fileName}</span>
                    </Styled.File>
                  ))}
                </Styled.Files>
              </Styled.VerticalLeftAligned>

              <Styled.Divider />
            </>
          )}

          <Styled.VerticalLeftAligned>
            <Styled.Title className="small">Akcje</Styled.Title>

            <Styled.Actions>
              <Styled.Button
                onClick={() => navigate(navigationPaths().CostInvoicesFormEdit.replace(':costId', costId))}
              >
                <Edit /> Edytuj
              </Styled.Button>

              <Styled.Button onClick={() => downloadSelectedFile()}>
                <FileDownload /> Pobierz plik
              </Styled.Button>

              <Styled.Button className="danger" onClick={() => setIsDeleteModal(true)}>
                <DeleteOutline /> Usuń
              </Styled.Button>
            </Styled.Actions>
          </Styled.VerticalLeftAligned>
        </Styled.SideCard>
      </Styled.Root>

      {isDeleteModal && (
        <DeleteConfirmationModal
          onClose={() => setIsDeleteModal(false)}
          onConfirm={deleteCost}
          isSaving={isCostDeleting}
        />
      )}
    </DashboardLayout>
  );
};
